import {createSelector} from 'reselect';
import {pathOr, trim} from 'ramda';

import {PaymentType} from 'constants/flow/flowTypes';
import {ApplicationStoreType} from 'reducers';
import {CurrentAddressType, ShippingMethodType} from 'constants/flow/flowTypesCheckout';
import {COUNTRIES} from 'constants/location';

import {CheckoutType, CurrentAddressWithoutRegionsType} from 'reducers/checkout/checkoutType';
import {PageAppType} from 'reducers/app/pageApp/pageAppType';
import {getCurrentPage} from 'selectors/app/app';
import {isProperty} from 'libs/utils/utils';
import {RegionsItemType} from 'reducers/location/locationType';
import {getRegionsByCountry} from 'selectors/location';

import {isContactLensesInOrder} from './orderReview/orderReview';

const ADDRESS_REGIONS_LIST_EMPTY = [];

export const valuesForMailbox = [' po ', 'p. o', 'p.o', ' p o ', 'po box', 'box'];

/**
 * Returns addresses list
 * @param {ApplicationStoreType} store - Current store
 * @returns {Array<{}>} - Stored addresses list
 */
export const getAddressesList = (store: ApplicationStoreType): Array<{}> =>
    store.addresses.addressesList;

/**
 * Returns form key customer
 * @param {ApplicationStoreType} store - Current store customer
 * @returns {string} - Stored form key
 */
export const getFormKey = (store: ApplicationStoreType): string => store.customer.form_key;

/**
 * Returns checkout data (totals and etc)
 * @param {ApplicationStoreType} store - Current checkout data
 * @returns {CheckoutType} - Stored checkout datasagas/checkout/checkout.js
 */
export const getCheckoutData = (store: ApplicationStoreType): CheckoutType => store.checkout;

/**
 * Get active payment method
 * @param {ApplicationStoreType} store - Current store customer
 * @returns {PaymentType} - active payment method
 */
export const getActivePaymentMethod = (store: ApplicationStoreType): PaymentType =>
    store.paymentMethodActive;

/**
 * Get payment method fulfilled
 * @param {ApplicationStoreType} store - Current store customer
 * @returns {boolean} - is payment method fulfilled
 */
export const getIsPaymentMethodFulfilled: (s: ApplicationStoreType) => boolean = isProperty(
    'paymentMethods.status',
    'fulfilled',
);

/**
 * Get payment method code
 * @param {ApplicationStoreType} store - Current store customer
 * @returns {string} - payment method code
 */
export const paymentMethodCode = (store: ApplicationStoreType): string =>
    store.paymentMethodActive.code;

/**
 * Get active shipping method
 * @param {ApplicationStoreType} store - Current store customer
 * @returns {ShippingMethodType} - active shipping method
 */
export const getActiveShippingMethod = (store: ApplicationStoreType): ShippingMethodType =>
    store.shippingMethodActive;

/**
 * How many customer clicked on payment methods
 * @param {ApplicationStoreType} store - Current store customer
 * @returns {number} - How many customer clicked on payment methods
 */
export const getCounterPayment = (store: ApplicationStoreType): number =>
    store.checkout.counterPayment;

/**
 * Returns current billing customer's address without regions
 * @param {ApplicationStoreType} store - Current store
 * @returns {CurrentAddressType} - address
 */
export const getCurrentBillingAddressWithoutRegions = (
    store: ApplicationStoreType,
): CurrentAddressWithoutRegionsType => store.currentBillingAddress;

/**
 * Returns country code from the current billing address
 */
export const getCurrentBillingAddressCountryCode = createSelector(
    getCurrentBillingAddressWithoutRegions,
    (billingAddress): string => billingAddress.data.country,
);

/**
 * Returns list of the regions for the current billing address
 * @param {string} countryCode - country's code for which return a list of regions
 * @returns {RegionsItemType[]} - regions list
 */
export const getCurrentBillingAddressRegions = createSelector(
    getCurrentBillingAddressCountryCode,
    store => store,
    (countryCode, store: ApplicationStoreType): RegionsItemType[] =>
        getRegionsByCountry(store, countryCode) || ADDRESS_REGIONS_LIST_EMPTY,
);

/**
 * Returns current shipping customer's address
 * @param {ApplicationStoreType} store - Current store
 * @returns {CurrentAddressType} - address
 */
export const getCurrentBillingAddress = createSelector(
    getCurrentBillingAddressWithoutRegions,
    getCurrentBillingAddressRegions,
    (currentAddressWithoutRegions, regionsList): CurrentAddressType => ({
        ...currentAddressWithoutRegions,
        data: {
            ...currentAddressWithoutRegions.data,
            regionsList,
        },
    }),
);

/**
 * Returns current shipping customer's address
 * @param {ApplicationStoreType} store - Current store
 * @returns {CurrentAddressType} - address
 */
export const getCurrentShippingAddressWithoutRegions = (
    store: ApplicationStoreType,
): CurrentAddressWithoutRegionsType => store.currentShippingAddress;

/**
 * Returns country code from the current shipping address
 */
export const getCurrentShippingAddressCountryCode = createSelector(
    getCurrentShippingAddressWithoutRegions,
    (shippingAddress): string => shippingAddress.data.country,
);

/**
 * Is it a country noneligible for contacts?
 */
export const isContactsUnprocessableCountry = createSelector(
    getCurrentShippingAddressCountryCode,
    isContactLensesInOrder,
    (countryCode, isContacts): boolean => countryCode !== COUNTRIES.UNITED_STATES && isContacts,
);

/**
 * Returns list of the regions for the current shipping address
 * @param {string} countryCode - country's code for which return a list of regions
 * @returns {RegionsItemType[]} - regions list
 */
export const getCurrentShippingAddressRegions = createSelector(
    getCurrentShippingAddressCountryCode,
    store => store,
    (countryCode, store): RegionsItemType[] =>
        getRegionsByCountry(store, countryCode) || ADDRESS_REGIONS_LIST_EMPTY,
);

/**
 * Returns current shipping customer's address
 * @param {ApplicationStoreType} store - Current store
 * @returns {CurrentAddressType} - address
 */
export const getCurrentShippingAddress = createSelector(
    getCurrentShippingAddressWithoutRegions,
    getCurrentShippingAddressRegions,
    (currentAddressWithoutRegions, regionsList): CurrentAddressType => ({
        ...currentAddressWithoutRegions,
        data: {
            ...currentAddressWithoutRegions.data,
            regionsList,
        },
    }),
);

/**
 * Returns shipping methods
 * @param {ApplicationStoreType} store - Current store
 * @returns {ShippingMethodType[]} - shipping methods count
 */
export const getShippingMethods = (store: ApplicationStoreType): ShippingMethodType[] =>
    store.shippingMethod.list;

/**
 * Get boolean when checking the address and other fields to match the given strings in the array
 * @param {CurrentAddressType} data - current address
 * @return {boolean} match found or not
 */
export const isMailbox = createSelector(
    getCurrentShippingAddress,
    ({data: {street, aptSuiteOther, country}}: CurrentAddressType): boolean => {
        return (
            country === COUNTRIES.UNITED_STATES &&
            valuesForMailbox.some(exception =>
                `${street.toLowerCase()}|${aptSuiteOther.toLowerCase()}`.includes(exception),
            )
        );
    },
);

/**
 * Get boolean if shipping methods === 1 or there is a mailbox in the address fields
 * @param {ShippingMethodType[]} shippingMethodList - shipping methods
 * @param {boolean} isMailbox - presence of a mailbox
 * @return {boolean} match found or not
 */
export const isSingleShippingMethod = createSelector(
    getShippingMethods,
    isMailbox,
    (shippingMethodList, mailbox): boolean => shippingMethodList.length === 1 || mailbox,
);

/**
 * Returns full name from billing information on checkout
 */
export const getBillingAddressFullName = createSelector(
    getCurrentBillingAddress,
    ({data: {firstName, lastName}}: CurrentAddressType): string => trim(`${firstName} ${lastName}`),
);

/**
 * Returns card details by passed payment method token
 */
export const getBillingAddressEmail = createSelector(
    getCurrentBillingAddress,
    ({data: {email}}: CurrentAddressType): string | undefined => email,
);

/**
 * Returns customer phone
 */
export const getBillingAddressPhone = createSelector(
    getCurrentBillingAddress,
    ({data: {phone}}: CurrentAddressType): Maybe<string> => phone,
);

/**
 * Returns checkout type
 * @param {ApplicationStoreType} store - Current store
 * @returns {string} checkout type
 */
export const getCheckoutType = (store: ApplicationStoreType): string => store.pageApp.params.type;

/**
 * Check location is checkout page
 */
export const isCheckoutPage = createSelector(
    getCurrentPage,
    ({page}: PageAppType): boolean => page === 'checkout',
);

/**
 * Get checkout totals
 */
export const getCheckoutGrandTotal = createSelector(
    getCheckoutData,
    checkoutData => checkoutData.totals.grandTotal,
);

/**
 * Gets items of checkout`s totals
 */
export const getCheckoutTotalsItems = createSelector(getCheckoutData, checkoutData =>
    pathOr([], ['totals', 'items'], checkoutData),
);

/**
 * Returns flag that indicates active state of CA
 *
 * @param {ApplicationStoreType} store - application store
 * @return {boolean} california state selected flag
 */
export const isCaliforniaCurrentShippingAddressState = createSelector(
    getCurrentShippingAddress,
    (currentShippingAddress: CurrentAddressType): boolean =>
        currentShippingAddress.data.regionCode === 'CA',
);

/**
 * Returns current step of checkout
 *
 * @param {ApplicationStoreType} store - application store
 * @return {number} code of the current checkout step
 */
export const getCheckoutCurrentStep = (store: ApplicationStoreType) => store.checkout.currentStep;
